import React, { useEffect, useRef, useState }  from 'react'
import { View, StyleSheet } from 'react-native'
import { Icon, Text, CheckBox, useTheme } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import OperationsList from '../../components/OperationsList'
import HeaderIconButton from '../../components/HeaderIconButton'
import { clientOrderStatuses, statusDisplayText, orderIsProcessed } from '../../utils/orderStatus'

export default function ClientOrdersScreen({ navigation }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const [showActive, setShowActive] = useState(true)
  const operationsListRef = useRef()

  useEffect(() => {
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('Home')
            }}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
  }, [])
  
  const STATUSES = clientOrderStatuses(t)

  const renderItemFieldsToShow = (item, date) => {
    // ATL-1103: `status` is the Firestore map on both read paths now, but rows that never had one
    // return a bare id. Reading `item.isProcessed` directly is not enough either — the Supabase
    // response carried no such field at all until this ticket, so every order read as unprocessed.
    const isProcessed = typeof item.isProcessed === 'boolean'
      ? item.isProcessed
      : orderIsProcessed(item.status)
    return (
      <>
        <View style={styles.dateTimeView}>
          <Icon name='person-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {item.name}
          </Text>
        </View>

        <View style={styles.dateTimeView}>
          <Icon name='phone-call-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {item.phone}
          </Text>
        </View>
        
        <View style={styles.dateTimeView}>
          <Icon name='clock-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {date}
          </Text>
        </View>

        <View style={styles.dateTimeView}>
          <Icon name='bell-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'
            status={isProcessed === false && 'danger'}>
            {isProcessed ? t('clientOrders.processed') : t('clientOrders.notProcessed')}
          </Text>
        </View>

        <View style={styles.dateTimeView}>
          <Icon name='activity-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {/* ATL-1103: `item?.status?.text` was undefined for a bare-id status, so this
                rendered "new" for EVERY order — a CANCELLED one included. */}
            {statusDisplayText(item?.status, STATUSES) ?? t('clientOrders.statusNew')}
          </Text>
        </View>
        
      </>
    )
  }

  // ATL-986: declared for BOTH read paths. Before, the Supabase read path dropped
  // this and the "active only" list also showed delivered and cancelled orders.
  const activeFilter = {
    firestore: (unsubscribe) => unsubscribe.where('isProcessed', '==', false),
    api: { isProcessed: false },
  }

  const noFilter = {
    firestore: (unsubscribe) => unsubscribe,
    api: {},
  }

  const topView = () => {
    return (
      <CheckBox style={styles.dateFilter} checked={showActive} onChange={(value) => {
        setShowActive(value)
        if (value)
          operationsListRef.current.refresh('allTime', activeFilter)
        else
          operationsListRef.current.refresh({startDate: new Date(), endDate: new Date()}, noFilter)
      }}>
        {t('clientOrders.showActive')}
      </CheckBox>
    )
  }

  return (
    <OperationsList collectionName='orders' dateField={'date'} additionalFilters={showActive ? activeFilter : null} 
      detailScreenName={'ClientOrderDetail'} topView={topView} initialRange={'allTime'}
      showTotalSum={false} totalSumFormatter={null} totalSumField={null} ref={operationsListRef}
      showAddButton={false} additionalDataValidation={() => {}} detailScreenParams={{}} preselectedStore={'all'}
      renderItemFieldsToShow={renderItemFieldsToShow} navigation={navigation} showStoreFilter={true}
    />
  )
}

const styles = StyleSheet.create({
  dateFilter: {
    marginHorizontal: 20,
    marginTop: 10
  },
  icon: {
    marginRight: 4,
  },
  itemTitle: {
    marginRight: 25,
  },
  dateTimeView: {
    flexDirection: 'row',
    alignItems: 'center'
  },
});
